import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate or retrieve session ID from sessionStorage
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
};

// Get chatbot session ID if exists
const getChatbotSessionId = (): string | null => {
  return sessionStorage.getItem('chatbot_session_id');
};

// Detect device type from user agent
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

// Parse UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
};

// Get referrer path (internal navigation)
const getReferrerPath = (): string | null => {
  const referrer = document.referrer;
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (url.origin === window.location.origin) {
      return url.pathname;
    }
  } catch {
    // Invalid URL
  }
  return null;
};

// Detect country code client-side via ipapi.co, cached in sessionStorage
let countryPromise: Promise<string | null> | null = null;

const detectCountryCode = (): Promise<string | null> => {
  // Return cached value immediately if available
  const cached = sessionStorage.getItem('tracking_country_code');
  if (cached) return Promise.resolve(cached === 'UNKNOWN' ? null : cached);

  // Deduplicate concurrent calls
  if (!countryPromise) {
    countryPromise = (async () => {
      try {
        const res = await fetch('https://ipapi.co/country/', {
          signal: AbortSignal.timeout(3000),
        });
        if (res.ok) {
          const code = (await res.text()).trim();
          if (code && code.length === 2 && !code.includes('{')) {
            sessionStorage.setItem('tracking_country_code', code);
            return code;
          }
        }
      } catch {
        // Geolocation failed
      }
      sessionStorage.setItem('tracking_country_code', 'UNKNOWN');
      return null;
    })();
  }
  return countryPromise;
};

export const usePageTracking = () => {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const pageLoadTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip if same path (e.g., query param changes)
    if (currentPath === lastPathRef.current) return;

    // Track the page view via edge function (to capture country from IP)
    const trackPageView = async () => {
      const sessionId = getSessionId();
      const chatbotSessionId = getChatbotSessionId();
      const utmParams = getUtmParams();
      const referrerPath = lastPathRef.current || getReferrerPath();
      const countryCode = await detectCountryCode();

      // Get current user if logged in
      const { data: { user } } = await supabase.auth.getUser();

      const pageViewData = {
        session_id: sessionId,
        user_id: user?.id || null,
        page_path: currentPath,
        page_title: document.title,
        referrer: document.referrer || null,
        referrer_path: referrerPath,
        user_agent: navigator.userAgent,
        device_type: getDeviceType(),
        chatbot_session_id: chatbotSessionId,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        country_code: countryCode,
      };

      try {
        await supabase.functions.invoke('track-page-view', {
          body: pageViewData,
        });
      } catch (error) {
        console.error('Failed to track page view:', error);
      }
    };

    trackPageView();
    
    // Update refs for next navigation
    lastPathRef.current = currentPath;
    pageLoadTimeRef.current = Date.now();
  }, [location.pathname]);
};

// Export function to set chatbot session ID (call from chatbot component)
export const setChatbotSessionId = (sessionId: string) => {
  sessionStorage.setItem('chatbot_session_id', sessionId);
};

// Export function to get current tracking session ID
export const getTrackingSessionId = (): string => {
  return getSessionId();
};
